class IndexDbService {
  constructor() {
    this.dbPromises = {};
  }

  isSupported() {
    return typeof window !== "undefined" && typeof window.indexedDB !== "undefined";
  }

  getDbCacheKey(dbName, version) {
    return `${dbName}:${version}`;
  }

  hasMissingStores(db, stores = []) {
    return stores.some(storeConfig => !db.objectStoreNames.contains(storeConfig.name));
  }

  createMissingStores(db, stores = []) {
    stores.forEach(storeConfig => {
      if (!db.objectStoreNames.contains(storeConfig.name)) {
        db.createObjectStore(storeConfig.name, { keyPath: storeConfig.keyPath || "id" });
      }
    });
  }

  invalidateDbPromises(dbName) {
    Object.keys(this.dbPromises).forEach(cacheKey => {
      if (cacheKey.startsWith(`${dbName}:`)) {
        delete this.dbPromises[cacheKey];
      }
    });
  }

  deleteDatabase(dbName) {
    if (!this.isSupported()) {
      return Promise.resolve(true);
    }

    this.invalidateDbPromises(dbName);

    return new Promise(resolve => {
      try {
        const request = window.indexedDB.deleteDatabase(dbName);
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
        request.onblocked = () => resolve(false);
      } catch (error) {
        resolve(false);
      }
    });
  }

  upgradeDb({ dbName, currentVersion, stores = [] }) {
    this.invalidateDbPromises(dbName);

    return new Promise(resolve => {
      try {
        const request = window.indexedDB.open(dbName, currentVersion + 1);

        request.onupgradeneeded = event => {
          const db = event.target.result;
          this.createMissingStores(db, stores);
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      } catch (error) {
        resolve(null);
      }
    });
  }

  openDb({ dbName, version = 1, stores = [] }) {
    if (!this.isSupported()) {
      return Promise.resolve(null);
    }

    const cacheKey = this.getDbCacheKey(dbName, version);
    if (this.dbPromises[cacheKey] != null) {
      return this.dbPromises[cacheKey];
    }

    this.dbPromises[cacheKey] = new Promise(resolve => {
      try {
        const request = window.indexedDB.open(dbName, version);

        request.onupgradeneeded = event => {
          const db = event.target.result;
          this.createMissingStores(db, stores);
        };

        request.onsuccess = async () => {
          const db = request.result;
          if (this.hasMissingStores(db, stores)) {
            const currentVersion = db.version;
            db.close();
            const upgradedDb = await this.upgradeDb({ dbName, currentVersion, stores });
            resolve(upgradedDb);
            return;
          }
          resolve(db);
        };
        request.onerror = () => resolve(null);
      } catch (error) {
        resolve(null);
      }
    });

    return this.dbPromises[cacheKey];
  }

  runStoreOperation({ dbName, version = 1, storeName, stores = [], mode = "readonly", handler }) {
    return this.openDb({ dbName, version, stores }).then(db => {
      if (db == null) {
        return null;
      }

      return new Promise(resolve => {
        try {
          const transaction = db.transaction(storeName, mode);
          const store = transaction.objectStore(storeName);
          handler(store, resolve);
          transaction.onerror = () => resolve(null);
        } catch (error) {
          resolve(null);
        }
      });
    });
  }

  async get({ dbName, version = 1, storeName, stores = [], key }) {
    return this.runStoreOperation({
      dbName,
      version,
      storeName,
      stores,
      mode: "readonly",
      handler: (store, resolve) => {
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => resolve(null);
      }
    });
  }

  async set({ dbName, version = 1, storeName, stores = [], value }) {
    return this.runStoreOperation({
      dbName,
      version,
      storeName,
      stores,
      mode: "readwrite",
      handler: (store, resolve) => {
        const request = store.put(value);
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
      }
    });
  }

  async delete({ dbName, version = 1, storeName, stores = [], key }) {
    return this.runStoreOperation({
      dbName,
      version,
      storeName,
      stores,
      mode: "readwrite",
      handler: (store, resolve) => {
        const request = store.delete(key);
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
      }
    });
  }

  async getAll({ dbName, version = 1, storeName, stores = [] }) {
    const result = await this.runStoreOperation({
      dbName,
      version,
      storeName,
      stores,
      mode: "readonly",
      handler: (store, resolve) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
        request.onerror = () => resolve([]);
      }
    });

    return Array.isArray(result) ? result : [];
  }
}

export default new IndexDbService();
